/**
 * SEO Infrastructure
 *
 * Centraliserad hantering av meta-titlar, beskrivningar och Open Graph-taggar.
 * Använd PageMeta-komponenten på varje sida för konsekvent SEO.
 */

import { useEffect } from "react";
import { Helmet } from "react-helmet-async";

// ═══════════════════════════════════════════════════════════════════
// Configuration
// ═══════════════════════════════════════════════════════════════════

export const SEO_CONFIG = {
  siteName: "Kvanten",
  siteUrl: "https://kvanten.se",
  defaultTitle: "Kvanten - Sveriges enklaste verktyg för K2-årsredovisning",
  defaultDescription:
    "Ladda upp SIE-fil och få färdig årsredovisning på minuter. Automatisk K2-validering, PDF i Bolagsverkets format. Betala endast vid export.",
  defaultImage: "/og-image.png", // TODO: Skapa Open Graph-bild
  twitterHandle: "@kvanten_se", // TODO: Uppdatera med faktiskt handle
  locale: "sv_SE",
  type: "website",
} as const;

// ═══════════════════════════════════════════════════════════════════
// SEO Metadata per page category
// ═══════════════════════════════════════════════════════════════════

/**
 * Publika sidor - KRITISKA för SEO
 * Dessa indexeras av sökmotorer
 */
export const PUBLIC_PAGE_META = {
  landing: {
    title: "Kvanten - Årsredovisning, Aktiebok & Protokoll för småföretag",
    description:
      "Komplett plattform för K2-årsredovisning från 2 495 kr. Digital aktiebok, automatiska protokoll, integrationer med Fortnox & Spiris. Spara 80% jämfört med traditionell byrå.",
    keywords:
      "K2 årsredovisning, årsredovisning online, digital aktiebok, årsstämmoprotokoll, SIE-fil, bokslut online, Bolagsverket, billig årsredovisning, utan revisor, småföretag bokslut, Fortnox integration, automatisk årsredovisning, PDF årsredovisning, K2 K3 bokslut, revisorsplikt, eget bokslut",
  },
  pricing: {
    title: "Priser - Kvanten",
    description:
      "Enkel prissättning: 2 495 kr per företag och räkenskapsår. Ingen prenumeration, betala endast när du exporterar. Rabatt för byråer.",
    keywords: "pris årsredovisning, kostnad K2, billig årsredovisning, byrå rabatt",
  },
  features: {
    title: "Funktioner - Kvanten",
    description:
      "SIE4-parsing, automatisk K2-validering, notgenerering, PDF enligt Bolagsverkets format, XML-export. Intelligent wizard anpassad efter din SIE-fil.",
    keywords: "SIE-parser, K2-validering, noter årsredovisning, PDF export, XML Bolagsverket",
  },
  about: {
    title: "Om Kvanten",
    description:
      "Kvanten utvecklades för att göra årsredovisning enklare för svenska småföretag. Regeldriven automatisering istället för manuellt arbete.",
    keywords: "om Kvanten, svensk årsredovisning, K2 mjukvara",
  },
  contact: {
    title: "Kontakt - Kvanten",
    description: "Kontakta oss för frågor om Kvanten eller K2-årsredovisning. Vi hjälper dig gärna.",
    keywords: "kontakt Kvanten, support årsredovisning",
  },
} as const;

/**
 * Auth-sidor - Lägre SEO-prioritet men bör ha titlar
 */
export const AUTH_PAGE_META = {
  login: {
    title: "Logga in - Kvanten",
    description: "Logga in på ditt Kvanten-konto för att hantera dina årsredovisningar.",
    noIndex: true, // Ska ej indexeras
  },
  register: {
    title: "Skapa konto - Kvanten",
    description: "Skapa ett gratis Kvanten-konto och börja med din årsredovisning direkt.",
    noIndex: true,
  },
  forgotPassword: {
    title: "Glömt lösenord - Kvanten",
    description: "Återställ ditt lösenord för ditt Kvanten-konto.",
    noIndex: true,
  },
  verifyEmail: {
    title: "Verifiera e-post - Kvanten",
    description: "Verifiera din e-postadress för att aktivera ditt Kvanten-konto.",
    noIndex: true,
  },
  acceptInvitation: {
    title: "Acceptera inbjudan - Kvanten",
    description: "Du har blivit inbjuden till ett företag i Kvanten.",
    noIndex: true,
  },
} as const;

/**
 * Dashboard-sidor - Helt privata, ska aldrig indexeras
 * Titlar är optimerade för att vara tydliga i webbläsarflikar
 */
export const DASHBOARD_PAGE_META = {
  dashboard: {
    title: "Översikt",
    description: "Din översikt i Kvanten",
  },
  companies: {
    title: "Mina företag",
    description: "Hantera dina företag i Kvanten",
  },
  companyDetail: (companyName: string) => ({
    title: companyName,
    description: `Årsredovisningar för ${companyName}`,
  }),
  newReport: (companyName: string) => ({
    title: `Ny årsredovisning · ${companyName}`,
    description: `Skapa ny årsredovisning för ${companyName}`,
  }),
  settings: {
    title: "Kontoinställningar",
    description: "Hantera dina kontoinställningar",
  },
  billing: {
    title: "Betalningar",
    description: "Hantera betalning och prenumerationer",
  },
  notifications: {
    title: "Notifikationer",
    description: "Dina meddelanden och aviseringar",
  },
  wizard: (step: string, companyName?: string) => ({
    title: companyName ? `${step} · ${companyName}` : `Årsredovisning · ${step}`,
    description: "Skapa din K2-årsredovisning steg för steg",
  }),
} as const;

/**
 * Admin-sidor - Helt privata
 */
export const ADMIN_PAGE_META = {
  dashboard: { title: "Admin - Dashboard" },
  users: { title: "Admin - Användare" },
  companies: { title: "Admin - Företag" },
  reports: { title: "Admin - Rapporter" },
  shareBooks: { title: "Admin - Aktieböcker" },
  protocols: { title: "Admin - Protokoll" },
  subscriptions: { title: "Admin - Prenumerationer" },
  roles: { title: "Admin - Roller" },
  settings: { title: "Admin - Inställningar" },
  pricing: { title: "Admin - Prissättning" },
  auditLog: { title: "Admin - Systemloggar" },
  statistics: { title: "Admin - Statistik" },
} as const;

// ═══════════════════════════════════════════════════════════════════
// Page Meta Component
// ═══════════════════════════════════════════════════════════════════

interface PageMetaProps {
  /** Sidans titel (läggs till " - Kvanten" automatiskt för interna sidor) */
  title: string;
  /** Meta-beskrivning för sökmotorer */
  description?: string;
  /** Sökord (kommaseparerade) */
  keywords?: string;
  /** Open Graph-bild URL */
  image?: string;
  /** Kanonisk URL (för att undvika duplicate content) */
  canonicalUrl?: string;
  /** Typ av innehåll för Open Graph */
  type?: "website" | "article" | "product";
  /** Om sidan ska undantas från sökmotorindex */
  noIndex?: boolean;
  /** Om titeln redan är komplett (för publika sidor) */
  fullTitle?: boolean;
}

/**
 * Komponent för att sätta meta-taggar på en sida.
 *
 * @example
 * // Publik sida med full SEO
 * <PageMeta
 *   title="Kvanten - Sveriges enklaste verktyg för K2-årsredovisning"
 *   description="Ladda upp SIE-fil och få färdig årsredovisning..."
 *   keywords="K2, årsredovisning, SIE"
 *   fullTitle
 * />
 *
 * @example
 * // Dashboard-sida (läggs till " - Kvanten" automatiskt)
 * <PageMeta title="Dashboard" noIndex />
 *
 * @example
 * // Dynamisk sida
 * <PageMeta title={`${company.name}`} noIndex />
 */
export function PageMeta({
  title,
  description = SEO_CONFIG.defaultDescription,
  keywords,
  image = SEO_CONFIG.defaultImage,
  canonicalUrl,
  type = "website",
  noIndex = false,
  fullTitle = false,
}: PageMetaProps) {
  const pageTitle = fullTitle ? title : `${title} - ${SEO_CONFIG.siteName}`;
  const imageUrl = image.startsWith("http") ? image : `${SEO_CONFIG.siteUrl}${image}`;

  // Fallback: Sätt document.title direkt för att garantera att titeln uppdateras
  // (react-helmet-async kan ha kompatibilitetsproblem med React 19)
  useEffect(() => {
    document.title = pageTitle;
  }, [pageTitle]);

  return (
    <Helmet>
      {/* Grundläggande meta */}
      <title>{pageTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}

      {/* Robots */}
      {noIndex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow" />
      )}

      {/* Canonical */}
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}

      {/* Open Graph (Facebook, LinkedIn) */}
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={type} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:site_name" content={SEO_CONFIG.siteName} />
      <meta property="og:locale" content={SEO_CONFIG.locale} />
      {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl} />
      {SEO_CONFIG.twitterHandle && (
        <meta name="twitter:site" content={SEO_CONFIG.twitterHandle} />
      )}

      {/* Språk */}
      <html lang="sv" />
    </Helmet>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Utility functions
// ═══════════════════════════════════════════════════════════════════

/**
 * Genererar en säker sidtitel (max 60 tecken för SEO)
 */
export function truncateTitle(title: string, maxLength = 60): string {
  if (title.length <= maxLength) return title;
  return title.substring(0, maxLength - 3) + "...";
}

/**
 * Genererar en säker meta-beskrivning (max 160 tecken för SEO)
 */
export function truncateDescription(description: string, maxLength = 160): string {
  if (description.length <= maxLength) return description;
  return description.substring(0, maxLength - 3) + "...";
}

/**
 * Genererar strukturerad data för Organisation (JSON-LD)
 */
export function getOrganizationSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Kvanten",
    url: SEO_CONFIG.siteUrl,
    logo: `${SEO_CONFIG.siteUrl}/logo.svg`,
    description: SEO_CONFIG.defaultDescription,
    address: {
      "@type": "PostalAddress",
      addressCountry: "SE",
    },
  };
}

/**
 * Genererar strukturerad data för SoftwareApplication (JSON-LD)
 */
export function getSoftwareApplicationSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "Kvanten",
    operatingSystem: "Web",
    applicationCategory: "BusinessApplication",
    offers: {
      "@type": "Offer",
      price: "2495",
      priceCurrency: "SEK",
      priceValidUntil: new Date(new Date().getFullYear() + 1, 11, 31).toISOString().split("T")[0],
    },
    // OBS: aggregateRating läggs till när vi har faktiska kundrecensioner
    description: SEO_CONFIG.defaultDescription,
  };
}

/**
 * Genererar strukturerad data för Product/Service jämförelse (JSON-LD)
 */
export function getProductSchema(priceExclVat: number | null) {
  const price = priceExclVat ?? 2495;
  return {
    "@context": "https://schema.org",
    "@type": "Product",
    name: "K2-årsredovisning med Kvanten",
    description:
      "Komplett K2-årsredovisning inklusive PDF i Bolagsverkets format, XML för digital inlämning, automatisk validering och 7 års arkivering.",
    brand: {
      "@type": "Brand",
      name: "Kvanten",
    },
    offers: {
      "@type": "Offer",
      price: price.toString(),
      priceCurrency: "SEK",
      priceValidUntil: new Date(new Date().getFullYear() + 1, 11, 31).toISOString().split("T")[0],
      availability: "https://schema.org/InStock",
      seller: {
        "@type": "Organization",
        name: "Kvanten",
      },
    },
    // OBS: aggregateRating läggs till när vi har faktiska kundrecensioner
  };
}

/**
 * Genererar FAQ strukturerad data (JSON-LD)
 */
export function getFAQSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "Vem får göra sin egen årsredovisning utan revisor?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Företag som uppfyller minst två av tre villkor under de senaste två åren får göra egen årsredovisning: högst 3 anställda (i medeltal), högst 1,5 miljoner kr i balansomslutning, eller högst 3 miljoner kr i nettoomsättning. Detta gäller majoriteten av svenska småföretag.",
        },
      },
      {
        "@type": "Question",
        name: "Vad kostar det att göra årsredovisning med Kvanten?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Kvanten kostar 2 495 kr exkl. moms per företag och räkenskapsår. Detta inkluderar PDF i Bolagsverkets format, XML för digital inlämning, automatisk K2-validering och 7 års arkivering. Jämfört med en traditionell byrå som tar 8 000-25 000 kr sparar du upp till 90%.",
        },
      },
      {
        "@type": "Question",
        name: "Hur fungerar Kvanten?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Du laddar upp din SIE-fil från ditt bokföringsprogram (Fortnox, Spiris, etc.), vår wizard guidar dig genom kompletterande uppgifter, och sedan genereras en komplett årsredovisning automatiskt. Hela processen tar vanligtvis under 30 minuter.",
        },
      },
      {
        "@type": "Question",
        name: "Vad ingår utöver årsredovisningen?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Kvanten-plattformen inkluderar även digital aktiebok enligt ABL 5 kap, automatiskt genererade årsstämmoprotokoll, företagshälsa med nyckeltal och varningar, samt integrationer med Bolagsverket, Fortnox och Spiris.",
        },
      },
      {
        "@type": "Question",
        name: "Är Kvanten säkert att använda?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Ja, Kvanten följer svenska lagar och regler (ÅRL, BFNAR 2016:10 K2). All data lagras säkert i Sverige med kryptering och automatisk backup. Årsredovisningen valideras automatiskt mot K2-regelverket.",
        },
      },
    ],
  };
}

/**
 * Genererar BreadcrumbList strukturerad data (JSON-LD)
 */
export function getBreadcrumbSchema(items: Array<{ name: string; url: string }>) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: item.url.startsWith("http") ? item.url : `${SEO_CONFIG.siteUrl}${item.url}`,
    })),
  };
}

/**
 * Genererar WebSite strukturerad data med sökfunktion (JSON-LD)
 */
export function getWebSiteSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "Kvanten",
    url: SEO_CONFIG.siteUrl,
    description: SEO_CONFIG.defaultDescription,
    inLanguage: "sv-SE",
    publisher: {
      "@type": "Organization",
      name: "Kvanten",
      logo: {
        "@type": "ImageObject",
        url: `${SEO_CONFIG.siteUrl}/logo.svg`,
      },
    },
  };
}

/**
 * Genererar LocalBusiness strukturerad data (JSON-LD)
 */
export function getLocalBusinessSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    name: "Kvanten",
    description:
      "Digital plattform för K2-årsredovisning, aktiebok och protokoll för svenska småföretag.",
    url: SEO_CONFIG.siteUrl,
    logo: `${SEO_CONFIG.siteUrl}/logo.svg`,
    priceRange: "$$",
    address: {
      "@type": "PostalAddress",
      addressCountry: "SE",
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: "59.3293",
      longitude: "18.0686",
    },
    areaServed: {
      "@type": "Country",
      name: "Sverige",
    },
    serviceType: ["Årsredovisning", "Digital aktiebok", "Protokollhantering", "Bokslut"],
  };
}
